import React, { ReactNode, useMemo } from 'react';
import { cn } from '@fabric-ui/utils';
import { Box, Divider, SxProps, Theme, Typography } from '@mui/material';
import Markdown, { Options as MarkdownOptions } from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import { SKIP, visit } from 'unist-util-visit';
import CopyableLink from './CopyableLink';

const headingStyle = { fontWeight: 600, mb: 0, mt: 3 };
const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})/;
const CLOSING_FENCE_LINE = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
const SCRIPT_URL = /^\s*javascript:/i;
const ROW_TAGS = new Set(['p', 'pre', 'table', 'blockquote', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

type AvMarkdownVariant = 'document' | 'rows';

const rowIndentationClassName =
  '[&_li_li>p]:pl-8 [&_li_li_li>p]:pl-16 [&_li_li_li_li>p]:pl-24 [&_li_li_li_li_li>p]:pl-32 [&_li_li_li_li_li_li>p]:pl-40 [&_li_li_li_li_li_li_li>p]:pl-48';

const collapseBlankLinesOutsideFences = (content: string): string => {
  const kept: string[] = [];
  content
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .reduce<string | undefined>((openFence, line) => {
      if (openFence) {
        kept.push(line);
        const closing = line.match(CLOSING_FENCE_LINE)?.[1];
        return closing && closing[0] === openFence[0] && closing.length >= openFence.length ? undefined : openFence;
      }
      const fence = line.match(FENCE_LINE)?.[1];
      if (fence !== undefined || line.trim().length > 0) {
        kept.push(line);
      }
      return fence;
    }, undefined);

  return kept.join('\n').trim();
};

type MarkdownNode = {
  type: string;
  children?: MarkdownNode[];
  [key: string]: unknown;
};

const remarkSplitRows = () => (tree: MarkdownNode) => {
  const transform = (node: MarkdownNode): MarkdownNode[] => {
    if (node.type === 'paragraph' && node.children?.some(child => child.type === 'break')) {
      const rows = node.children.reduce<MarkdownNode[][]>(
        (result, child) => {
          if (child.type === 'break') {
            result.push([]);
          } else {
            result[result.length - 1].push(child);
          }
          return result;
        },
        [[]]
      );
      return rows.filter(row => row.length > 0).map(children => ({ ...node, children }));
    }

    const children = node.children?.flatMap(transform);
    return [{ ...node, ...(children ? { children } : {}), ...(node.type === 'listItem' ? { spread: true } : {}) }];
  };

  return transform(tree)[0];
};

const rehypeMarkRows = () => (tree: any) => {
  const rows: any[] = [];
  visit(tree, 'element', (node: any) => {
    if (!ROW_TAGS.has(node.tagName)) return undefined;
    rows.push(node);
    return SKIP;
  });
  const hasDivider = String(rows.length > 1);
  rows
    .filter(node => node.tagName === 'p')
    .forEach(node => Object.assign(node, { properties: { ...node.properties, dataHasDivider: hasDivider } }));
};

type NestedListNode = {
  text: string;
  children: NestedListNode[];
};

const parseNestedListLines = (content: string): { indent: number; text: string }[] =>
  content
    .replace(/\u00A0/g, ' ')
    .split('\n')
    .map(line => line.replace(/[ \t]+$/, ''))
    .filter(line => line.trim().length > 0)
    .map(line => {
      const match = line.match(/^([ \t]*)([-*+•–—]|\d+\.)\s+(.+)$/);

      if (!match) {
        return null;
      }

      return {
        indent: match[1].length,
        text: match[3].trim(),
      };
    })
    .filter((item): item is { indent: number; text: string } => Boolean(item));

const buildNestedListTree = (items: { indent: number; text: string }[]): NestedListNode[] => {
  const root: NestedListNode[] = [];
  const stack: { indent: number; children: NestedListNode[] }[] = [{ indent: -1, children: root }];

  items.forEach(item => {
    while (stack.length > 1 && item.indent <= stack[stack.length - 1].indent) {
      stack.pop();
    }

    const node: NestedListNode = { text: item.text, children: [] };
    stack[stack.length - 1].children.push(node);
    stack.push({ indent: item.indent, children: node.children });
  });

  return root;
};

const toMarkdownListLines = (items: NestedListNode[], depth = 0): string[] =>
  items.flatMap(item => [`${'  '.repeat(depth)}- ${item.text}`, ...toMarkdownListLines(item.children, depth + 1)]);

const adaptNestedListContent = (content: string): string => {
  const normalizedText = content.replace(/\u00A0/g, ' ');
  const nonEmptyLines = normalizedText
    .split('\n')
    .map(line => line.replace(/[ \t]+$/, ''))
    .filter(line => line.trim().length > 0);
  const parsedLines = parseNestedListLines(normalizedText);
  const isNestedList = nonEmptyLines.length > 0 && parsedLines.length === nonEmptyLines.length;

  if (!isNestedList) {
    return normalizedText;
  }

  return toMarkdownListLines(buildNestedListTree(parsedLines)).join('\n');
};

const stripMailtoPrefix = (href: string): string => href.replace(/^mailto:/i, '');

const extractLinkLabel = (children: ReactNode): string | undefined => {
  const text = React.Children.toArray(children)
    .map(child => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('')
    .trim();
  return text || undefined;
};

const buildMarkdownComponents = (nestedList: boolean) => ({
  h1: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  h2: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  h3: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  h4: ({ node, ...props }: { node?: any }) => <Box sx={headingStyle} {...props} />,
  p: ({ node, ...props }: { node?: any }) => <Typography variant="body1" sx={{ mt: 1 }} {...props} />,
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <CopyableLink link={stripMailtoPrefix(href ?? '')} label={extractLinkLabel(children)} />
  ),
  hr: ({ node, ...props }: { node?: any }) => <Divider sx={{ my: 2, borderColor: 'var(--color-border-base-secondary)' }} {...props} />,
  ul: ({ node, ...props }: { node?: any }) => (
    <Box
      component="ul"
      sx={
        nestedList
          ? {
              pl: 3,
              m: 0,
              listStyleType: 'disc',
              listStylePosition: 'outside',
              '& ul': {
                pl: 3,
                listStyleType: 'circle',
              },
            }
          : {
              pl: 3,
              m: 0,
              listStyleType: 'disc',
              listStylePosition: 'outside',
              '& ul': { pl: 3, listStyleType: 'circle' },
            }
      }
      {...props}
    />
  ),
  ol: ({ node, ...props }: { node?: any }) => (
    <Box
      component="ol"
      sx={{
        pl: 3,
        mt: 0,
        mb: 1,
        listStyleType: 'decimal',
        listStylePosition: 'outside',
        '& ol': { pl: 3, listStyleType: 'lower-alpha' },
      }}
      {...props}
    />
  ),
  li: ({ node, ...props }: { node?: any }) => <Box component="li" sx={{ m: 0, '& p': { mt: 0 }, '& p ~ p': { mt: 1 } }} {...props} />,
  blockquote: ({ node, ...props }: { node?: any }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: `4px solid var(--color-surface-interactive-secondary-default)`,
        pl: 2,
        ml: 0,
        my: 2,
        fontStyle: 'italic',
      }}
      {...props}
    />
  ),
  pre: ({ node, children, ...props }: { node?: any; children?: any }) => (
    <Box
      component="pre"
      aria-label="code-block"
      sx={theme => ({
        background: 'var(--color-surface-base-tertiary)',
        borderRadius: theme.shape.borderRadius,
        fontFamily: 'monospace',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        px: 1,
        py: 0.5,
      })}
      {...props}>
      {children}
    </Box>
  ),
  code: ({ node, children, ...props }: { node?: any; children?: any }) => (
    <Box
      component="code"
      sx={theme => ({
        background: 'var(--color-surface-base-tertiary)',
        borderRadius: theme.shape.borderRadius,
        fontFamily: 'monospace',
      })}
      {...props}>
      {children}
    </Box>
  ),
  table: ({ node, ...props }: { node?: any }) => (
    <Box
      component="table"
      sx={{
        width: '100%',
        my: 2,
        borderCollapse: 'collapse',
      }}
      {...props}
    />
  ),
  thead: ({ node, ...props }: { node?: any }) => (
    <Box component="thead" sx={{ background: 'var(--color-surface-base-secondary)' }} {...props} />
  ),
  tr: ({ node, ...props }: { node?: any }) => (
    <Box component="tr" sx={{ borderBottom: '1px solid var(--color-border-base-primary)' }} {...props} />
  ),
  th: ({ node, ...props }: { node?: any }) => <Box component="th" sx={{ textAlign: 'left', p: 1.5, fontWeight: 600 }} {...props} />,
  td: ({ node, ...props }: { node?: any }) => <Box component="td" sx={{ p: 1.5, verticalAlign: 'top' }} {...props} />,
});

const defaultMarkdownComponents = buildMarkdownComponents(false);
const nestedListMarkdownComponents = buildMarkdownComponents(true);

const rowComponents = {
  p: ({ node, className, 'data-has-divider': hasDivider, ...props }: { node?: any; className?: string; 'data-has-divider'?: string }) => (
    <Box
      component="p"
      data-testid="av-markdown-row"
      data-has-divider={hasDivider}
      className={cn('m-0 py-2', hasDivider === 'true' && 'border-0 border-b border-solid border-border-base-primary', className)}
      {...props}
    />
  ),
  ul: ({ node, className, ...props }: { node?: any; className?: string }) => (
    <Box component="ul" role="list" className={cn('m-0 list-none p-0', className)} {...props} />
  ),
  ol: ({ node, className, ...props }: { node?: any; className?: string }) => (
    <Box component="ol" role="list" className={cn('m-0 list-none p-0', className)} {...props} />
  ),
  li: ({ node, className, ...props }: { node?: any; className?: string }) => (
    <Box component="li" className={cn('m-0 p-0', className)} {...props} />
  ),
};

const DIRECTIVE_NODE_TYPES = new Set(['textDirective', 'leafDirective', 'containerDirective']);

const remarkDirectiveToHast = () => (tree: any) => {
  // unist-util-visit transforms the tree in place; updating the visited node is the intended remark pattern.
  visit(tree, (node: any) => {
    if (DIRECTIVE_NODE_TYPES.has(node.type)) {
      Object.assign(node, { data: { ...node.data, hName: node.name, hProperties: node.attributes || {} } });
    }
  });
};

type DirectiveDefinition = {
  directive: string;
  renderer: (props: Record<string, string>) => ReactNode;
  allowedAttributes?: string[];
};

const DEFAULT_DIRECTIVE_ATTRIBUTES = ['text', 'href', 'src', 'label', 'value', 'id', 'type', 'name'];

const buildSanitizeSchema = (directives: DirectiveDefinition[]) => {
  if (!directives.length) return defaultSchema;
  return {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), ...directives.map(d => d.directive)],
    attributes: {
      ...defaultSchema.attributes,
      ...Object.fromEntries(directives.map(d => [d.directive, d.allowedAttributes ?? DEFAULT_DIRECTIVE_ATTRIBUTES])),
    },
  };
};

const hoistTablesFromListItems = (content: string): string => content?.replace(/(^|\n)- (\*\*[^*\n]+\*\*)\n(\|)/g, '$1$2\n\n$3');

const unescapeLiteralNewlines = (content: string): string => content?.replace(/\\n/g, '\n');

const preserveBlankLinesBeforeListItems = (content: string): string =>
  content?.replace(
    /\n(?:[ \t]*\n)+(?=[ \t]*(?:[-*+]|\d+[.)])[ \t]+)/g,
    match => `\n${'\u00A0\n'.repeat((match.match(/\n/g)?.length ?? 1) - 1)}`
  );

const AvMarkdown = ({
  content,
  sx = {},
  nestedList = false,
  directives = [],
  variant = 'document',
}: {
  content: string;
  sx?: SxProps<Theme>;
  nestedList?: boolean;
  directives?: DirectiveDefinition[];
  variant?: AvMarkdownVariant;
}) => {
  const isRows = variant === 'rows';
  const processedContent = useMemo(() => {
    const unescaped = unescapeLiteralNewlines(nestedList ? adaptNestedListContent(content) : content);
    return isRows
      ? collapseBlankLinesOutsideFences(hoistTablesFromListItems(unescaped))
      : preserveBlankLinesBeforeListItems(hoistTablesFromListItems(unescaped));
  }, [content, nestedList, isRows]);

  // Rejects event-handler props and javascript: URLs coming from directive attributes.
  const isSafeProp = ([key, value]: [string, unknown]): boolean =>
    !key.toLowerCase().startsWith('on') && !(typeof value === 'string' && SCRIPT_URL.test(value));

  const components = useMemo(() => {
    const base = nestedList ? nestedListMarkdownComponents : defaultMarkdownComponents;
    const withVariant = isRows ? { ...base, ...rowComponents } : base;
    if (!directives.length) return withVariant;
    const directiveComponents = Object.fromEntries(
      directives.map(({ directive, renderer }) => [
        directive,
        ({ node, ...props }: { node?: any; [key: string]: any }) =>
          renderer(Object.fromEntries(Object.entries(props).filter(isSafeProp)) as Record<string, string>),
      ])
    );
    return { ...withVariant, ...directiveComponents };
  }, [nestedList, directives, isRows]);

  const remarkPlugins = useMemo(
    () => [remarkGfm, remarkBreaks, remarkDirective, remarkDirectiveToHast, ...(isRows ? [remarkSplitRows] : [])],
    [isRows]
  );

  const rehypePlugins = useMemo<MarkdownOptions['rehypePlugins']>(
    () => [[rehypeSanitize, buildSanitizeSchema(directives)], ...(isRows ? [rehypeMarkRows] : [])],
    [directives, isRows]
  );

  return (
    <Box className={cn(isRows && rowIndentationClassName)} sx={{ wordBreak: 'break-word', ...sx }}>
      <Markdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
        {processedContent}
      </Markdown>
    </Box>
  );
};

export type { AvMarkdownVariant, DirectiveDefinition };
export { AvMarkdown };
